/************************************************/
/*                About lz4js                   */
/************************************************/
// ISC License
//
// Copyright 2016 Benzinga
//
// Permission to use, copy, modify, 
// and/or distribute this software for any purpose with or without fee is hereby granted, 
// provided that the above copyright notice and this permission notice appear in all copies.
//
// THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH REGARD TO THIS SOFTWARE 
// INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS. 
// IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES 
// WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, 
// NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.

const lz4 = require("lz4js");

import logWrapper from "../../../../../share/logWrapper";
import dmDidDataConversion from "../../../../../share/util/didDataConversion";
import dmUtil from "../../../dataMonitor/dataMonitorUtil";
import { fixNumberWithDecimal } from "../../../../../common/nodejs/mathFunctions";

const DATAPOINT_PAYLOAD = {
  TYPE_LENGTH: 1,
  TIME_LENGTH: 3,
  ID_LENGTH: 2,
};

// WS.146で使用される、演算用DIDの配列
export const WS146_OTHER_CALCULATION_DID = ['2F01', '2F02', '2F03', '2F04', '2F05', '2F06', '2F07', '2F08'];

export default class DmDataPointConversion {

  constructor() {
    this.isDebug = process.env.VUE_APP_DEBUG_LOG && process.env.VUE_APP_DEBUG_LOG === "true";
    this.funcValues = {};
    this.dataTotalCount = 0;
  }

  clear() {
    this.funcValues = {};
    this.dataTotalCount = 0;
  }

  /**
   * データ変換
   * @param {object} dataPointModel データポイント
   * @param {array} conversionKeys 変換情報のキー配列
   * @param {array} conversionValues 変換情報の値の配列
   * @param {object} didConversionMap 変換情報のDIDごとのインデックスを持つオブジェクト
   */
  dataConvert(dataPointModel, conversionKeys, conversionValues, didConversionMap) {

    if (!conversionKeys || !conversionKeys.length) return [];

    const res = [];
    const indexes = didConversionMap[dataPointModel.data_id];

    indexes.forEach(index => {
      const conversionInfo = conversionValues[index];
      const itemId = conversionKeys[index];

      const [convertedVal, originHexString] = this.dataConversion(
        conversionInfo,
        dataPointModel,
        itemId
      );

      // 項目ごとの値を更新
      this.updateFuncValues(
        itemId,
        convertedVal,
        conversionInfo
      );

      // データ件数の通知
      this.dataTotalCount++;

      // For Debug: 総件数の出力
      if (this.dataTotalCount % 2000 === 0) {
        logWrapper.log(`data point total counts[${this.dataTotalCount}]`);
      }

      res.push({
        data_monitor_id: itemId, // 計測データID
        convertedData: convertedVal, // 変換済み計測値
        originHexString: this.isDebug ? originHexString : '',
        time: this.getTimeFromMeasureStart(dataPointModel.time), // 計測時間
      });
    });
    return res;
  }

  /**
   * 計測経過時間(ミリ秒)を取得
   * @param {String} YYYY-MM-DDTHH:mmss.sssssZ
   * @returns {number} ミリ秒
   */
  getTimeFromMeasureStart(rfcTime) {
    return new Date(rfcTime).getTime();
  }

  /**
   * data_id(DID/PID)を取得
   * @param {String} "0/PU_EGI/F401" or "0/EPS_PH4/01"
   * @returns {String} "F401" or "01"
   */
  getDataIdFourDigit(dataId) {
    const tmpList = dataId.split("/");
    return tmpList[tmpList.length - 1];
  }

  /**
   * 項目ごとの保管データ更新
   * @param {*} id data_monitor_id
   * @param {*} val 変換済の値
   * @param {*} conversionInfo 変換情報
   */
  // eslint-disable-next-line complexity
  updateFuncValues(id, val, conversionInfo) {
    const unitPrefix = conversionInfo.is_obd ? `obd_unit.` : `didUnit.`;
    const unit = unitPrefix + conversionInfo.unit_id;
    const data_type = conversionInfo.data_type;
    const digit_after_decimal = conversionInfo.digit_after_decimal;
    let convertValue;

    // funcValuesに項目ごとにデータ格納
    // 項目の初回
    if (!this.funcValues[id]) {
      this.funcValues[id] = {
        cnt: 1,
        unit: unit,
        data_type: data_type,
        digit_after_decimal: digit_after_decimal
      };

      // 最大/最小はdecExStrとValueとACL2INCのみ
      // 平均はdecExStrとValueのみ
      switch (data_type) {
        case "Value":
        case "CALCULATION_DID": // 演算系DID固有のdata_type
          this.funcValues[id].max = Number(val); // 最大
          this.funcValues[id].maxDisp = val;
          this.funcValues[id].min = Number(val); // 最小
          this.funcValues[id].minDisp = val;
          this.funcValues[id].avg = Number(val); // 平均
          this.funcValues[id].avgDisp = fixNumberWithDecimal(Number(val), digit_after_decimal);
          break;
        case "decExStr":
          {
            // HEX値を数値に変換する
            convertValue = Number(dmDidDataConversion.convertValueDataForHex(val, conversionInfo));

            // メイン処理内で翻訳を行うため、表示値にHEX値のまま設定
            this.funcValues[id].max = convertValue; // 最大
            this.funcValues[id].maxDisp = val;
            this.funcValues[id].min = convertValue; // 最小
            this.funcValues[id].minDisp = val;
            this.funcValues[id].avg = convertValue; // 平均
            this.funcValues[id].avgDisp = fixNumberWithDecimal(convertValue, digit_after_decimal);
          }
          break;
        case "ACL2INC":
          this.funcValues[id].max = Number(val); // 最大
          this.funcValues[id].maxDisp = val;
          this.funcValues[id].min = Number(val); // 最小
          this.funcValues[id].minDisp = val;
          this.funcValues[id].avgDisp = "-"; // 平均
          break;
        case "HEX":
          convertValue = parseInt(val, 16);
          this.funcValues[id].max = convertValue; // 最大
          this.funcValues[id].maxDisp = "-";
          this.funcValues[id].min = convertValue; // 最小
          this.funcValues[id].minDisp = "-";
          this.funcValues[id].avgDisp = "-"; // 平均
          break;
        case "FLG":
          this.funcValues[id].max = 255; // 最大
          this.funcValues[id].maxDisp = "-";
          this.funcValues[id].min = 0; // 最小
          this.funcValues[id].minDisp = "-";
          this.funcValues[id].avgDisp = "-"; // 平均
          break;
        default:
          if (dmUtil.VALUE_CONVERT_DECIMAL_TYPE.includes(data_type)) {
            convertValue = dmDidDataConversion.convertBinToInt(val, conversionInfo);
            this.funcValues[id].max = convertValue; // 最大
            this.funcValues[id].min = convertValue; // 最小
          }
          this.funcValues[id].maxDisp = "-"; // 最大
          this.funcValues[id].minDisp = "-"; // 最小
          this.funcValues[id].avgDisp = "-"; // 平均
          break;
      }
    }
    // 項目の2回目以降
    else {
      this.funcValues[id].cnt++;

      // 最大/最小はdecExStrとValueとACL2INCのみ
      switch (data_type) {
        case "decExStr":
          {
            // HEX値を数値に変換する
            convertValue = Number(dmDidDataConversion.convertValueDataForHex(val, conversionInfo));

            // メイン処理内で翻訳を行うため、表示値にHEX値のまま設定
            if (this.funcValues[id].max < convertValue) {
              this.funcValues[id].max = convertValue; // 最大
              this.funcValues[id].maxDisp = val;
            }
            if (this.funcValues[id].min > convertValue) {
              this.funcValues[id].min = convertValue; // 最小
              this.funcValues[id].minDisp = val;
            }
          }
          break;
        case "ACL2INC":
        case "Value":
        case "CALCULATION_DID": // 演算系DID固有のdata_type
          convertValue = Number(val);

          if (this.funcValues[id].max < convertValue) {
            this.funcValues[id].max = convertValue; // 最大
            this.funcValues[id].maxDisp = fixNumberWithDecimal(convertValue, digit_after_decimal);
          }
          if (this.funcValues[id].min > convertValue) {
            this.funcValues[id].min = convertValue; // 最小
            this.funcValues[id].minDisp = fixNumberWithDecimal(convertValue, digit_after_decimal);
          }
          break;
        case "HEX":
          convertValue = parseInt(val, 16);
          if (this.funcValues[id].max < convertValue) {
            this.funcValues[id].max = convertValue; // 最大
          }
          if (this.funcValues[id].min > convertValue) {
            this.funcValues[id].min = convertValue; // 最小
          }
          break;
        case "FLG":
          // FLGは最大と最小が固定のため何もしない
          break;
        default:
          if (dmUtil.VALUE_CONVERT_DECIMAL_TYPE.includes(data_type)) {
            convertValue = dmDidDataConversion.convertBinToInt(val, conversionInfo);
            if (this.funcValues[id].max < convertValue) {
              this.funcValues[id].max = convertValue; // 最大
            }
            if (this.funcValues[id].min > convertValue) {
              this.funcValues[id].min = convertValue; // 最小
            }
          }
          break;
      }

      // 平均はdecExStrとValueと演算系DID固有のCALCULATION_DIDのみ
      if (data_type === "decExStr" || data_type === "Value" || data_type === "CALCULATION_DID") {
        const newAvg = this.funcValues[id].avg + (convertValue - this.funcValues[id].avg) / this.funcValues[id].cnt;
        this.funcValues[id].avg = newAvg; // 平均
        this.funcValues[id].avgDisp = fixNumberWithDecimal(newAvg, digit_after_decimal);
      }
    }
  }

  /**
   * データ変換処理。
   * ただし値を10進数変換してグラフ描画を行うデータタイプの項目は、表示直前に変換を行うため、バイト配列で返す。
   * 
   * @param {object} conversionInfo 変換情報
   * @param {object} dataPointModel APIから取得した計測値
   * @param {string} conversionId 対象DID
   * @returns {array[string|array[byte]]} 変換後の値と16進数変換のみした値
   */
  dataConversion(conversionInfo, dataPointModel, conversionId) {
    const measuredData = this.findData(
      conversionInfo,
      dataPointModel,
      conversionId
    );

    const byteDataList = [];
    if (conversionInfo.byte_or_bit === "byte") {
      for (let i = 0; i < measuredData.length; i += 2) {
        byteDataList.push(parseInt(measuredData.substring(i, i + 2), 16));
      }
    } else {
      byteDataList.push(...this.hexToDecimalArray(parseInt(measuredData, 2).toString(16)));
    }

    let convertedValue = "";
    // DIDが2F01~2F08の場合、特殊処理に流す
    const did = dmUtil.getDid(dmUtil.getNoSystemItemId(conversionId));
    if (WS146_OTHER_CALCULATION_DID.includes(did)) {
      convertedValue = this.otherCalculationDid(byteDataList, conversionInfo);
    } else if (conversionInfo.data_type === "decExStr") {
      // グラフの値に使用するため、文字列に翻訳せずにHEXで返す
      convertedValue = dmDidDataConversion.convertHexData(byteDataList);
    } else if (dmUtil.VALUE_CONVERT_DECIMAL_TYPE.includes(conversionInfo.data_type)) {
      // グラフに10進数値と変換値を渡すため、表示直前に変換を行えるようにバイト配列のまま返す
      convertedValue = byteDataList;
    } else {
      // 変換値の取得
      convertedValue = dmDidDataConversion.convertDataToDisplayValue(
        byteDataList,
        conversionInfo
      );
    }

    return [convertedValue, dmDidDataConversion.convertHexData(byteDataList)];
  }

  /**
   * WS.146で使用する演算系DIDを変換する特殊処理
   * @param {Array} byteDataList bufData
   * @param {object} conversionInfo 変換情報
   * @returns {string|Number} convertedValue 変換後の値
   */
  otherCalculationDid(byteDataList, conversionInfo) {
    let displayValue = "";
    switch (conversionInfo.data_type) {
      case 'FLG': {
        // FLGの場合はbyteDataListの最後列で判断する
        const dataByte = byteDataList[byteDataList.length - 1].toString(16).padStart(2, "0").toUpperCase();
        displayValue = dataByte === '00' || dataByte === 'FF' ? dataByte : `0x${dataByte}`;
        break;
      }
      case 'Value': {
        // Valueの場合もbyteDataListの最後列で判断する
        const dataByte = byteDataList[byteDataList.length - 1];
        const lastElementBuffer = new ArrayBuffer(1); // 1バイトのArrayBufferを作成
        const lastElementView = new Uint8Array(lastElementBuffer);
        lastElementView[0] = dataByte;
        displayValue = dmDidDataConversion.convertValueData(lastElementView, conversionInfo);
        break;
      }
      // 演算系DIDでしか使用されない特殊data_typeとして定義
      case 'CALCULATION_DID': {
        // 8byte double型
        const uint8View = new Uint8Array(byteDataList);
        const buffer = uint8View.buffer;
        const dataView = new DataView(buffer);
        // 特殊data_typeはリトルエンディアン固定
        const value = dataView.getFloat64(0, true);
        displayValue = fixNumberWithDecimal(value, conversionInfo.digit_after_decimal);
        break;
      }
      default:
        break;
    }
    
    return displayValue;
  }

  /**
   * DID情報から該当する計測値を取得する
   * @param {object} conversionInfo 変換情報
   * @param {object} dataPointModel S3から取得した計測値
   * @returns {string} subjectData 変換対象データ
   */
  findData(conversionInfo, dataPointModel, conversionId) {
    // 選択項目のDID/PID部分
    const did = dmUtil.getDid(conversionId);

    // DID情報を比較し、計測値から変換対象の計測値を検索する
    if (did === this.getDataIdFourDigit(dataPointModel.data_id)) {
      // 変換対象データ
      const rawData = dataPointModel.data;

      // 切り出す単位が「byte」の場合
      if (conversionInfo.byte_or_bit === "byte") {
        return this.getByteDataForDid(
          rawData,
          conversionInfo.start_byte_position,
          conversionInfo.byte_size,
          conversionInfo.little_endian
        );
        // 切り出す単位が「bit」の場合
      } else {
        return this.getBinaryDataForDid(
          rawData,
          conversionInfo.start_bit_position,
          conversionInfo.start_byte_position,
          conversionInfo.bit_size,
          conversionInfo.little_endian
        );
      }
    }

    return "";
  }

  /**
   * 16進数を10進数の配列に変換する
   * @param {*} hexStr 16進数文字列
   * @returns 10進数のバイト配列
   */
  hexToDecimalArray(hexStr) {
    let tmpStr = hexStr;
    // 文字列の長さが奇数の場合、先頭に0を追加
    if (tmpStr.length % 2 !== 0) {
      tmpStr = '0' + tmpStr;
    }

    const result = [];
    for (let i = 0; i < tmpStr.length; i += 2) {
      const hexPair = tmpStr.slice(i, i + 2);
      result.push(parseInt(hexPair, 16));
    }

    return result;
  }

  /**
   * 16進数データをbyte単位でトリムする。
   * @param {string} value 16進数文字列
   * @param {num} start_byte_position 1Based
   * @param {num} byte_size トリムするbyte数
   * @returns {string} トリム結果。16進数文字列
   */
  getByteData(value, start_byte_position, byte_size) {
    // トリムするbyteの最初の位置と最後の位置を計算する
    const startByte = (start_byte_position - 1) * 2;
    const endByte = startByte + byte_size * 2;

    // １６進数の値を最初の位置と最後の位置でトリムする
    return value.substring(startByte, endByte);
  }

  /**
   * 16進数文字列から、Byte単位でトリムする。（※DIDエンディアン変換版）
   * @param {string} value 16進数文字列
   * @param {num} start_byte_position 1Based
   * @param {num} byte_size トリム長[byte]
   * @param {boolean} is_little_endian リトルエンディアン判定
   * @returns {string} トリム結果。16進数文字列
   */
  getByteDataForDid(value, start_byte_position, byte_size, is_little_endian = false) {
    const startByte = (start_byte_position - 1) * 2;
    const endByte = startByte + byte_size * 2;
    const subString = value.substring(startByte, endByte);
    if (!is_little_endian) {
      // ビックエンディアンの場合、そのまま返す
      return subString;
    }

    // リトルエンディアンの場合、バイトオーダー反転。16進数文字列なので2文字ずつ反転
    let reversed = '';
    const valueLength = subString.length;
    const valueTemp = subString.padStart(valueLength + valueLength % 2, '0');
    for (let i = 0; i < valueTemp.length; i += 2) {
      reversed = valueTemp.substring(i, i + 2) + reversed;
    }
    return reversed;
  }

  /**
   * 16進数データをbit単位でトリムする。
   * @param {string} value 16進数データ
   * @param {num} start_bit_position 0Based
   * @param {num} start_byte_position 1Based
   * @param {string} bit_size トリムするbit数
   * @returns {string} トリム結果。16進数文字列
   */
  getBinaryData(value, start_bit_position, start_byte_position, bit_size) {
    // １６進数を２進数に変換
    const binaryValue = this.hexToBinary(value);

    // トリムするbitの最初の位置と最後の位置を計算する
    const byteSize = Math.ceil((start_bit_position + bit_size) / 8);
    const LSByte = start_byte_position + byteSize - 1;
    const endBit = LSByte * 8 - start_bit_position;
    const startBit = endBit - bit_size;

    // ２進数の値を最初の位置と最後の位置でトリムして返却する
    return parseInt(binaryValue.substring(startBit, endBit), 2);
  }

  /**
   * 16進数文字列から、bit単位でトリムする。（※DIDエンディアン変換版）
   * @param {string} value 16進数文字列
   * @param {num} start_bit_position 0Based
   * @param {num} start_byte_position 1Based
   * @param {string} bit_size トリム長[bit]
   * @param {boolean} is_little_endian リトルエンディアン判定
   * @returns {string} トリム結果。バイナリ文字列
   */
  getBinaryDataForDid(value, start_bit_position, start_byte_position, bit_size, is_little_endian = false) {
    const byteSize = Math.ceil((start_bit_position + bit_size) / 8);

    // startBytePosition から ByteSize 分切り出して、byte order をビックエンディアンにする。
    const byteValue = this.getByteDataForDid(value, start_byte_position, byteSize, is_little_endian);

    const binaryValue = this.hexToBinary(byteValue);
    // 切り出した byteValue の startBitPosition が LSBit
    const endBit = binaryValue.length - start_bit_position;
    const startBit = endBit - bit_size;
    return binaryValue.substring(startBit, endBit);
  }

  /**
   * 16進数を2進数に変換
   * @param {*} hex 
   * @returns 
   */
  hexToBinary(hex) {
    const lookup = {
      "0": "0000",
      "1": "0001",
      "2": "0010",
      "3": "0011",
      "4": "0100",
      "5": "0101",
      "6": "0110",
      "7": "0111",
      "8": "1000",
      "9": "1001",
      A: "1010",
      B: "1011",
      C: "1100",
      D: "1101",
      E: "1110",
      F: "1111",
    };
    const hexTemp = hex.toUpperCase();
    let ret = "";
    for (let index = 0; index < hexTemp.length; ++index) {
      ret += lookup[hexTemp[index]];
    }
    return ret;
  }

  /**
   * 計測時刻の計算
   * @param {BigInt} elapsedTime 経過時間
   */
  static convertTime(elapsedTime, baseTime) {
    // 末尾の「n」を削除してBigIntからNumberに変換(ES2020以降に対応するまでの暫定対応)
    const elapsedTimeNum = Number(`${elapsedTime}`.replace('n', ''));
    // baseTimeをDateオブジェクトに変換
    const baseDate = new Date(baseTime);
    // elapsedTimeをミリ秒に変換し、this.baseTimeに加算
    const newTime = new Date(baseDate.getTime() + Number(elapsedTimeNum));
    return newTime.getTime();
  }

  /**
   * Uint8Arrayを数値に変換
   * @param {Uint8Array} uint8Array 
   * @returns {number} 引数を数値に変換した結果
   */
  static uint8ArrayToNumber(uint8Array) {
    let number = 0;
    for (let i = 0; i < uint8Array.length; i++) {
      number = (number << 8) | uint8Array[i];
    }
    return number;
  }

  /**
   * 呼び出される度に、DIDごとのデータを順に返すジェネレータ
   * @param {Uint8Array} compressedPayload lz4圧縮されたpayload。複数DIDのデータを持つ
   * @param {object} didSizeMap DIDごとのdataSizeをもったマップ
   * @param {string} systemName システム名
   * @returns {object} DIDごとのデータ
   */
  static * GetDidPayload(compressedPayload, didSizeMap, systemName) {
    /**
     * Payloadのフォーマット
     *  |StartBytePosition |Length                  |Description                            |
     *  |0                 |1                       |Type 0:DID,1:PID,2:RID                 |
     *  |1                 |3                       |ElapsedTime[ms]                        |
     *  |4                 |2                       |type=0:DID,1:PID,2:RID (WS.146はDID固定)|
     *  |6                 |variable length         |type=0,1:DataRecord,2:RoutineStatus    |
     */
    const payload = lz4.decompress(compressedPayload);
    let i = 0;
    while (i < payload.length) {
      const typeStartIndex = i;
      const type = payload[typeStartIndex];
      i += DATAPOINT_PAYLOAD.TYPE_LENGTH;
      // elapsed_timeはWS.146の処理では不要の為、スキッフ
      i += DATAPOINT_PAYLOAD.TIME_LENGTH;

      const idStartIndex = i;
      const idEndIndex = i + DATAPOINT_PAYLOAD.ID_LENGTH;

      const id = Array
        .from(payload.slice(idStartIndex, idEndIndex))
        .map(byte => byte.toString(16).toUpperCase().padStart(2, '0'))
        .join('');
      i += DATAPOINT_PAYLOAD.ID_LENGTH;

      // didSizeMapが複数システム用の場合は、IDにシステム名を付与
      const targetId = didSizeMap[id] && systemName ? id : dmUtil.setSystem(systemName, id);
      if (!didSizeMap[targetId]) {
        return;
      }

      const recordLength = didSizeMap[targetId];

      const valueStartIndex = i;
      const valueEndIndex = i + recordLength;
      const value = payload.slice(valueStartIndex, valueEndIndex);
      i += recordLength;

      yield {
        type: type,
        id: targetId,
        value: value
      };
    }
  }

  static __updateLastItems(lastItems, newData) {
    newData.forEach(data => {
      if (data.length) {
        // 参照を切るため別オブジェクトとして保持
        lastItems[data[0].data_monitor_id] = { ...data[0] };
      }
    });
  }

  /**
   * 最後の時間のデータがない項目の値を、空で埋めるためのデータを作成する
   * @param {object} lastItems 各項目の最終データ。処理内で上書きする
   * @param {number} lastTimestamp 最後のデータの時間
   * @param {array} didList 選択した項目の一覧
   * @returns {array} 穴埋めするためのデータ
   */
  static createEmptyFilling(lastItems, lastTimestamp, didList) {
    // 全選択項目の中で、直前のtimestampのデータがない項目をピックアップ
    const lackItems = didList.filter((val) => !Object.keys(lastItems).includes(val));

    // 最後のtimestampのデータがない項目は、空の値でその時間のデータを埋める
    const emptyFills = lackItems.map(did => ([{
      data_monitor_id: did,
      convertedData: null,
      time: lastTimestamp
    }]));

    // 項目の最終データを更新する
    DmDataPointConversion.__updateLastItems(lastItems, emptyFills);
    return emptyFills;
  }

  /**
   * 最後の時間のデータがない項目の値を、最後の値で埋めるためのデータを作成する
   * @param {object} lastItems 各項目の最終データ。処理内で上書きする
   * @param {number} lastTimestamp 最後のデータの時間
   * @return {array} 穴埋めするためのデータ
   */
  static createFilling(lastItems, lastTimestamp) {
    // 各項目の最終データのうち、直前のtimestampのデータがない項目をピックアップ
    const lackItems = Object.keys(lastItems)
      .map(lastItemDid => {
        return lastItems[lastItemDid].time && lastItems[lastItemDid].time !== lastTimestamp ?
          lastItems[lastItemDid] :
          null;
      })
      .filter(lastItem => lastItem);

    // 最後のtimestampのデータがない項目は、直前の値でその時間のデータを埋める
    const fillingData = lackItems.map(lackItem => {
      lackItem['time'] = lastTimestamp;
      return [lackItem];
    });

    // 項目の最終データを更新する
    DmDataPointConversion.__updateLastItems(lastItems, fillingData);
    return fillingData;
  }

  /**
   * 保持している最後のデータを更新する
   * @param {object} lastItems 各項目の最後に取得した値を持つオブジェクト。処理内で上書きする
   * @param {object} lastTimestamp 各項目の最後に取得した時間を持つオブジェクト。処理内で上書きする
   * @param {array} convertedData 最新のデータ
   * @param {string} systemName システム名
   * @param {array} itemIdsBySystem システムごとの選択項目のIDの配列を持つ
   * @returns {array} 最終データを更新した際に、穴埋めしたデータの配列
   */
  static updateLastData(lastItems, lastTimestamp, convertedData, systemName, itemIdsBySystem) {
    const resultFillingData = [];

    // システムのない単一システムの場合、最初の項目IDのリストを使用
    const itemIds = itemIdsBySystem[systemName] ? itemIdsBySystem[systemName] : Object.values(itemIdsBySystem)[0];

    for (const converted of convertedData) {
      if (!lastTimestamp[systemName]) {
        lastTimestamp[systemName] = converted.time;
      } else if (lastTimestamp[systemName] !== converted.time) {
        // データポイントの時間が変わった場合で、最終項目がまだない項目は、その時間に空の値を設定
        if (itemIds.length !== Object.keys(lastItems[systemName]).length) {
          const emptyFills = DmDataPointConversion.createEmptyFilling(
            lastItems[systemName], lastTimestamp[systemName], itemIds);
          resultFillingData.push(...emptyFills);
        }

        // データポイントの時間が変わった場合で、変わる前の時間でデータがない項目を、直前の値で埋める
        const fillingData = DmDataPointConversion.createFilling(
          lastItems[systemName], lastTimestamp[systemName]);
        resultFillingData.push(...fillingData);

        // システムの最後の時間を更新
        lastTimestamp[systemName] = converted.time;
      }

      // 対象項目の最後のデータを更新。参照を切るため別オブジェクトとして保持
      lastItems[systemName][converted.data_monitor_id] = { ...converted };
    }

    return resultFillingData;
  }
}
